import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { Button, ButtonSizes } from 'atoms';
import { PATHS } from 'router/config';
import { useLmsAccessBlock } from 'core/hooks';

/**
 * Global debtor warning (spec 0003). Shown on every LMS page when the student
 * is a confirmed debtor. Soft warning — content still loads and access is NOT
 * cut off yet; this warns the student that access WILL BE disabled unless the
 * overdue invoice is paid. The block is detected + confirmed in the ERP; the
 * LMS only reads `me.lmsAccessBlock`.
 */
export const AccessBlockBanner = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // NOTE: `reason` (the operator's internal audit note) is deliberately NOT
  // shown to the student — it can contain private staff comments. The student
  // sees only the generic message + the outstanding amount.
  const { isBlocked, overdueAmount, activeRole } = useLmsAccessBlock();

  // Only in a student session — a user who is also a Teacher/Methodist should
  // not see "your lessons are paused" while operating in another role. Null
  // activeRole falls back to showing (preserves the original behavior).
  const isStudentContext = !activeRole || activeRole === 'Student';

  if (!isBlocked || !isStudentContext) {
    return null;
  }

  return (
    <Banner role="alert">
      <Title>{t('accessBlock.title')}</Title>
      <Text>{t('accessBlock.text')}</Text>
      {overdueAmount ? <Amount>{t('accessBlock.amount', { amount: overdueAmount })}</Amount> : null}
      <ViewInvoicesButton
        size={ButtonSizes.sm}
        variant="outline"
        onClick={() => navigate(PATHS.invoices)}>
        {t('accessBlock.viewInvoices')}
      </ViewInvoicesButton>
    </Banner>
  );
});

// Themed via Colors tokens (adapts to dark mode) instead of hardcoded hex.
// Solid signal_red surface with white content; the CTA inverts (white fill,
// red text) for contrast on the red background.
const Banner = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin: 0 auto 16px;
  padding: 20px 24px;
  border-radius: 12px;
  background-color: ${Colors.signal_red};
  color: ${Colors.white};
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 8px;
`;

const Text = styled.div`
  font-size: 14px;
  line-height: 1.4;
`;

const Amount = styled.div`
  font-weight: 600;
  margin-top: 8px;
  color: ${Colors.white};
  opacity: 0.85;
`;

const ViewInvoicesButton = styled(Button)`
  && {
    width: auto;
    margin-top: 24px;
    background-color: ${Colors.white};
    color: ${Colors.signal_red};
    border: none;
    font-weight: 600;

    &:hover {
      background-color: ${Colors.bg_gray};
    }
  }
`;
